import { createRoot } from "react-dom/client";

/**
 * Runtime configuration bootstrap.
 *
 * The orchestrator bind-mounts `nerd_concierge.json` / `.js` from Vault over
 * `build/env.json` and `build/env.js`. A missing source file on the host becomes an
 * EMPTY mount, so either file may be blank: prefer `env.json`, fall back to `env.js`,
 * and with neither, derive the hosts from our own name
 * (`concierge.<env>.rhapsody.la` → `api.<env>.rhapsody.la`).
 *
 * The build must NOT ship an env.js/env.json of its own: mounting a missing host file
 * over a file that exists in the image stops the container from starting.
 *
 * On localhost with no configuration at all, the app boots in mock mode (the whole
 * lookout API served from memory) so the UI can be worked on without a backend.
 * Mock mode is refused anywhere else.
 *
 * Nothing here may statically import a module that reads `AppConfig`, hence the
 * dynamic `import("./app")`.
 */

type Config = Record<string, unknown>;

const LOCAL_HOSTS = ["localhost", "127.0.0.1", "[::1]"];

/** concierge.beta.rhapsody.la → the beta gateway and sign-in page. */
function derivedConfig(): Config | null {
  const match = location.hostname.match(
    /^concierge\.([a-z0-9-]+)\.rhapsody\.la$/
  );
  if (!match) return null;
  const env = match[1];
  return {
    api: {
      endpoint: `https://api.${env}.rhapsody.la`,
      routePrefix: "",
      production: env === "live",
    },
    app: { "401Redirect": `https://start.${env}.rhapsody.la/signin` },
    front: { hostname: location.origin, production: env === "live" },
    studio: { hostname: `https://ensemble.${env}.rhapsody.la` },
  };
}

function hasKeys(config: unknown): config is Config {
  return (
    config != null &&
    typeof config === "object" &&
    Object.keys(config as Config).length > 0
  );
}

function fail(container: HTMLElement, message: string, error?: unknown) {
  console.error(`[bootstrap] ${message}`, error);
  if (container.childElementCount === 0) container.textContent = message;
}

function mountApp(container: HTMLElement): void {
  const root = createRoot(container);
  import("./app")
    .then(({ App }) => root.render(<App />))
    .catch((error) => {
      // A deploy replaced the chunks under an open tab: reload once.
      const key = "concierge:chunk-reload";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        location.reload();
        return;
      }
      fail(container, "Concierge could not load. Refresh to try again.", error);
    });
}

async function loadConfig(): Promise<Config | null> {
  try {
    const response = await fetch(`${location.origin}/env.json`, {
      cache: "no-store",
    });
    if (response.ok) {
      const text = await response.text();
      const json = text.trim() ? JSON.parse(text) : null;
      if (hasKeys(json)) return json;
    }
  } catch (e) {
    // fall through to env.js
  }
  if (hasKeys(window._APP_CONFIG)) return window._APP_CONFIG as Config;
  return derivedConfig();
}

const container = document.getElementById("root");

if (container != null) {
  loadConfig().then((config) => {
    const isLocal = LOCAL_HOSTS.includes(location.hostname);
    if (config) {
      const endpoint = (config.api as Config | undefined)?.endpoint;
      if (endpoint === "mock" && !isLocal) {
        fail(container, "Mock configuration refused outside localhost.");
        return;
      }
      window._APP_CONFIG = config;
    } else if (isLocal) {
      window._APP_CONFIG = {
        api: { endpoint: "mock" },
        // Mock ids are invented; this only shows the deep links' shape.
        studio: { hostname: "https://ensemble.beta.rhapsody.la" },
      };
    } else {
      fail(container, "Concierge has no configuration. Contact engineering.");
      return;
    }
    sessionStorage.removeItem("concierge:chunk-reload");
    mountApp(container);
  });
}
